import { Grid, Heading } from "@chakra-ui/react";
import { Formik } from "formik";
import AppListHeader from "@/features/ui/app-list-header";
import React, { useCallback, useRef } from "react";
import { DEFAULT_SEASON } from "@/features/line-sheet-sets/app-season-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import {
  useOrderConfirmationInvoiceDumbTransaction
} from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-dumb-transaction";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useOrderConfirmationInvoiceFormValidationSchema
  from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-form-validation-schema";
import PriceUtils from "@/features/ui/utils/price-utils";
import { Brand, Company, Price, StoredObject } from "@/features/types";
import { LightOrderSheetSet } from "@/features/order-sheets/order-sheet.type";
import {
  AbstractInvoice,
  OrderConfirmationInvoice,
  PaymentMethod
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import { PriceInputValue } from "@/features/invoices/app-price-input";
import OrderConfirmationInvoiceFormModal
  from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-form-modal";
import { useRecoilValue } from "recoil";
import { exchangeCurrencyStore } from "@/shared/store/exchangeCurrencyStore";
import {
  CROSS_TRADER_REQUIRED_MESSAGE,
  isCrossTraderMissing,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";

export interface OrderConfirmationInvoiceFormFieldsModal {
  id?: number;
  number?: string;
  status?: OrderConfirmationInvoice["status"];
  isRemittedToBoutique?: boolean;
  isRemittedToAgency?: boolean;
  name?: string;
  season?: string;
  orderedBy?: Pick<Company, "id" | "name">;
  // 발행사 — 3자무역이면 3자무역사, 원래 부티크는 originallyIssuedBy
  issuedBy?: Company;
  originallyIssuedBy?: Company;
  // 3자무역 여부(화면에서만 쓴다)와 수수료율(%) — 디파짓을 수수료를 곱한 금액으로 발행한다
  isCrossTrade?: boolean;
  crossTradeFeeRate?: number;
  issuedThrough?: Pick<Company, "id" | "name">;
  issuedOn?: Date;
  paymentMethod?: PaymentMethod;
  exposedToBuyer: boolean;
  paidByAgency: boolean;
  publicComment?: string | null;
  privateComment?: string | null;
  exchangeRate?: number;
  bankFee?: PriceInputValue

  orderSheetSets: LightOrderSheetSet[];
  file?: File | Pick<StoredObject, "id" | "name">;
  // 인보이스 총액·총 디파짓 — 담당자가 인보이스 금액을 그대로 넣는다(브랜드별 금액은 서버가 비율로 나눈다)
  totalAmount?: PriceInputValue;
  totalRequiredAmount?: PriceInputValue;
  orderConfirmationInvoiceDetailList: (Omit<
    OrderConfirmationInvoice["orderConfirmationInvoiceDetailList"][number],
    | "amount"
    | "requiredAmount"
    | "brand"
    | "orderSheetSet"
    | "remittedAmount"
    | "expectedRemainingDepositAmount"
    | "actualRemainingDepositAmount"
  > & {
    amount?: PriceInputValue;
    requiredAmount?: PriceInputValue;
    brand?: Pick<Brand, "id" | "name">;
    orderSheetSet?: {
      id: number;
      lineSheetSet: {
        id: number;
        name: string;
      };
    };
  })[];
  proformaInvoices: { id: number }[];
  expectedRemainingDepositAmount?: PriceInputValue;
  creditInvoices: { id: number }[];
  depositInvoice?: { id: number };
  payments: {
    id: number;
    status: AbstractInvoice["status"];
    sender: Pick<Company, "id" | "name">;
  }[];
}

export default function UploadOrderConfirmationInvoicePageModal(
  {
    buyerId,
    boutiqueId,
    orderSheetSet,
    season,
    handleClose
  }: {
    buyerId: number;
    boutiqueId: number;
    orderSheetSet: LightOrderSheetSet;
    // 라인시트세트 시즌 — OC 시즌 기본값
    season?: string;
    handleClose: () => void;
  }
) {
  const { error: showError, warning: showWarning } = useAppToasts();
  const identity = useIdentity();
  const { tSentence, tTitle } = useI18nHelper();

  const exchangeCurrencyInfo = useRecoilValue(exchangeCurrencyStore);
  const initialExchangeRateRef = useRef(exchangeCurrencyInfo["EUR"]?.remittance_send_rate);

  const validateSchema = useOrderConfirmationInvoiceFormValidationSchema();

  const validate = useCallback(
    async (values: OrderConfirmationInvoiceFormFieldsModal) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
      if (isCrossTraderMissing(values)) {
        showWarning(CROSS_TRADER_REQUIRED_MESSAGE);
        return { issuedBy: CROSS_TRADER_REQUIRED_MESSAGE };
      }
    },
    [showError, showWarning, validateSchema]
  );

  const { navigate } = useRouterHelper();

  const { addCreate } = useOrderConfirmationInvoiceDumbTransaction();

  const handleSubmit = useCallback(
    async (values: OrderConfirmationInvoiceFormFieldsModal) => {
      if (identity?.company?.type) {
        await addCreate(
          {
            by: identity.company.type,
            companyId: identity.company.id,
          },
          {
            number: values.number!,
            season: values.season!,
            name: values.name!,
            issuedToCompany: values.orderedBy!,
            issuedByCompany: values.issuedBy!,
            originallyIssuedByCompany: values.isCrossTrade
              ? values.originallyIssuedBy ?? null
              : null,
            // 3자무역 수수료율은 더 이상 쓰지 않는다(디파짓을 인보이스 금액 그대로 넣는다)
            crossTradeFeeRate: null,
            issuedThroughCompany: values.issuedThrough!,
            issuedOn: values.issuedOn!,
            paidBy: values.paidByAgency ? "AGENCY" : "BUYER",
            paymentMethod: values.paymentMethod!,
            // 브랜드별 금액은 보내지 않는다 — 서버가 총액·총 디파짓을 연결 오더의 브랜드별 확정금액 비율로 나눠 저장한다(2026-10-30)
            totalAmount: PriceUtils.placeholder(values.totalAmount, "EUR") as Price,
            totalRequiredAmount: PriceUtils.placeholder(values.totalRequiredAmount, "EUR") as Price,
            orderConfirmationInvoiceDetailList: [],
            orderSheetSets: values.orderSheetSets.map((oss) => {
              return {
                id: oss.id,
              };
            }),
            isPublic: values.exposedToBuyer,
            publicComment: values.publicComment || null,
            privateComment: values.privateComment || null,
            file: values.file!,
            exchangeRate: values.exchangeRate || 1,
            bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
          }
        );
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 500);
      });
    },
    [addCreate, identity]
  );

  return (
    <Formik<OrderConfirmationInvoiceFormFieldsModal>
      initialValues={{
        id: -1,
        status: "NORMAL",
        season: season || DEFAULT_SEASON,
        issuedOn: (() => {
          const date = new Date();
          date.setHours(0, 0, 0, 0);
          return date;
        })(),
        exposedToBuyer: true,
        paymentMethod: "NORMAL",
        paidByAgency: true,
        publicComment: "",
        privateComment: "",
        orderSheetSets: [
          orderSheetSet
        ],
        orderConfirmationInvoiceDetailList: [
          //이걸 왜 넣은거지?
          // {
          //   id: -1,
          // },
        ],
        proformaInvoices: [],
        creditInvoices: [],
        payments: [],
        orderedBy: {
          id: buyerId,
          name: ''
        },
        issuedThrough: {
          id: 1,
          name: ""
        },
        issuedBy: {
          id: boutiqueId,
          name: '',
          type: "BOUTIQUE"
        },
        exchangeRate: initialExchangeRateRef.current,
        bankFee: PriceUtils.placeholder(
          {
            currency: "KRW",
            value: 43000,
          },
          "KRW"
        ),
      }}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      onSubmit={async (values, formikHelpers) => {
        return handleSubmit(values);
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"1fr"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <AppListHeader
              headers={[
                <Heading
                  key={"UploadOrderConfirmationInvoice"}
                  as={"h2"}
                  fontSize={"14px"}
                >
                  {tTitle("upload_order_confirmation_invoice")}
                </Heading>,
              ]}
              filters={[]}
              actions={[
                <AppButton
                  key={`Cancel`}
                  isDisabled={props.isSubmitting}
                  onClick={async () => {
                    handleClose();
                  }}
                >
                  {tTitle("cancel")}
                </AppButton>,
                <AppButton
                  key={`Upload`}
                  variant={"primary"}
                  isLoading={props.isSubmitting}
                  onClick={async () => {
                    const result = await props.submitForm();
                    // @ts-ignore
                    if (result) {
                      handleClose();
                    }
                  }}
                >
                  {tTitle("upload")}
                </AppButton>,
              ]}
              showNavigateToBack={false}
              emptyStackPage={"/order-confirmation-invoices"}
              blockNavigateToBack={props.isSubmitting || props.dirty}
            />
            <OrderConfirmationInvoiceFormModal formik={props} isEditing={true} />
          </Grid>
        );
      }}
    </Formik>
  );
}
